import { useState, useEffect, useImperativeHandle, forwardRef, Fragment } from "react";
// import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "./Loader";

export default forwardRef(function Table(
  { data, fetchFn, config, filter, loading, className, emptyMessage, defaultSort, maxRows },
  ref
) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState(defaultSort || "");
  // const [scroll, setScroll] = useState();
  const [list, setList] = useState(data || []);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState("asc");
  const [showNested, setShowNested] = useState([]);
  const pageSize = 40;
  const navigate = useNavigate();

  // useBottomScrollListener(() => {
  //   if (window.pageYOffset !== scroll) {
  //     setScroll(window.pageYOffset);
  //     if (!data) loadMore();
  //   }
  // });

  const fetchData = async (currentPage) => {
    if (!fetchFn) return;
    let result = await fetchFn({ pageSize, currentPage, sortKey, sortDirection, filter });

    if (result.status !== 200) return;

    setList((list) => list.concat(result.data));

    if (result.data.length === pageSize) {
      setShowLoadMore(true);
    } else {
      setShowLoadMore(false);
    }
  };

  const loadMore = () => {
    setCurrentPage(currentPage + 1);
    fetchData(currentPage + 1);
  };

  useEffect(() => {
    if (data) {
      setList(data);
    } else {
      setList([]);
    }

    setCurrentPage(0);
    fetchData(0, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey, sortDirection, filter, data]);

  const capitalize = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  let filteredData = list.filter((item, key) => {
    if (fetchFn) return true;
    for (let filterKey in filter) {
      if (filter[filterKey] === "true" || filter[filterKey] === "false") {
        // Check boolean
        if (!item[filterKey]) item[filterKey] = false;
        if (filter[filterKey] && item[filterKey].toString() !== filter[filterKey].toString()) {
          return false;
        }
      } else {
        // Check string

        // Check null
        if (!filter[filterKey]) continue;

        // Check array
        let allowedValues = filter[filterKey].split("||");
        if (allowedValues.length > 1) {
          let found = false;
          for (let i = 0; i < allowedValues.length; i++) {
            if (item[filterKey] && item[filterKey].toString() === allowedValues[i]) {
              found = true;
              break;
            }
          }
          if (!found) return false;
        } else {
          // Match string
          if (
            !item[filterKey] ||
            (item[filterKey].toLowerCase && item[filterKey].toLowerCase().indexOf(filter[filterKey].toLowerCase()) === -1)
          ) {
            return false;
          }
        }
      }
    }
    return true;
  });

  useImperativeHandle(ref, () => {
    return {
      filteredData,
    };
  }, [filteredData]);

  const headerRender = (
    <tr>
      {config.columns.map((col, indexHeader) => (
        <th
          onClick={() => setSortKeyFn(col.sort || col.key)}
          key={"header_" + indexHeader}
          className={
            (col.sort || col.key ? "sortable " : "") +
            (sortKey
              ? col.sort
                ? sortKey === col.sort
                  ? "sort " + sortDirection
                  : ""
                : sortKey === col.key
                  ? "sort " + sortDirection
                  : ""
              : "") +
            (col.className ? " " + col.className : "")
          }
        >
          {col.header || capitalize(col.key)}

          {sortKey === col.key && sortDirection === "asc" && (
            <span className="material-symbols-outlined">keyboard_arrow_down</span>
          )}
          {sortKey === col.key && sortDirection === "desc" && (
            <span className="material-symbols-outlined">keyboard_arrow_up</span>
          )}
        </th>
      ))}
    </tr>
  );

  const setSortKeyFn = (key) => {
    if (!key) return;
    if (sortKey === key) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortDirection("asc");
      }
    } else {
      setSortDirection("asc");
    }
    setSortKey(key);
  };

  const sortFn = (a, b) => {
    if (!sortDirection || !sortKey) return 0;
    let aVal, bVal;

    aVal = getAttr(a, sortKey);
    bVal = getAttr(b, sortKey);

    if (!aVal && !bVal) {
      return 0;
    }
    if (!aVal) {
      return 1;
    }
    if (!bVal) {
      return -1;
    }
    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortDirection === "desc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    if (aVal < bVal) return sortDirection === "asc" ? 1 : -1;
    if (aVal > bVal) return sortDirection === "asc" ? -1 : 1;
    return 0;
  };

  const getAttr = (obj, key) => {
    let keys = key.split(".");
    let val = obj;
    for (let k of keys) {
      if (val == null || k === "__proto__" || k === "constructor" || k === "prototype") return undefined;
      val = val[k]; // nosemgrep: prototype-pollution-loop
      if (val === undefined) {
        return;
      }
    }
    return val;
  };

  // Sort the full set first, then (optionally) cap how many rows render.
  // `maxRows` slices AFTER sort so consumers get the true top-N, not the first
  // N in insertion order. Omitting `maxRows` renders everything (default).
  const sortedData = filteredData.sort(sortFn);
  const visibleData = Number.isFinite(maxRows) ? sortedData.slice(0, maxRows) : sortedData;

  const rowsRender = visibleData.map((row, indexRow) => (
    <Fragment key={"row_" + indexRow}>
      <tr className={config.linkRow || config.onClickRow ? "pointer" : ""} onClick={() => onClickRow(row, indexRow)}>
        {config.columns.map((col, indexCol) => (
          <td
            key={"row_" + indexCol}
            data-col-key={col.key}
            // Surface the column header on each cell so mobile card-flow
            // CSS can prepend a label via `::before { content: attr(data-label) }`
            // without changing the per-page table config.
            data-label={typeof col.header === "string" ? col.header : capitalize(col.key)}
            className={col.className && col.className}
          >
            {col.template ? col.template(row, indexRow) : row[col.key]}
          </td>
        ))}
      </tr>
      {showNested[indexRow] && (
        <tr className="nested-row">
          <td colSpan={config.columns.length}>{showNested[indexRow]}</td>
        </tr>
      )}
    </Fragment>
  ));

  const toggleNested = (html, indexRow) => {
    if (showNested[indexRow]) {
      showNested[indexRow] = undefined;
      setShowNested([...showNested]);
    } else {
      showNested[indexRow] = html;
      setShowNested([...showNested]);
    }
  };

  const onClickRow = (row, indexRow) => {
    if (config.onClickRow) {
      config.onClickRow(row, indexRow, (html) => toggleNested(html, indexRow));
    } else if (config.linkRow) {
      navigate(config.linkRow(row, indexRow));
    }
  };

  return (
    <table className={className}>
      <thead>{headerRender}</thead>
      <tbody>
        {loading || !data ? (
          <tr className="loader-container">
            <td colSpan={config.columns.length}>
              <Loader text={t("common.loading")}></Loader>
            </td>
          </tr>
        ) : filteredData.length > 0 ? (
          rowsRender
        ) : (
          <tr className="empty-row">
            <td colSpan={config.columns.length} style={{ textAlign: "left" }}>
              {emptyMessage || t("common.noItemsToShow")}
            </td>
          </tr>
        )}
      </tbody>
      {showLoadMore && (
        <tfoot onClick={loadMore}>
          <tr>
            <td colSpan={config.columns.length} style={{ cursor: "pointer" }}>
              {t("common.loadMore")}
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  );
});
